import { cn } from "@babackapp/customer-ui/lib/utils";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { useLocation, useOutlet } from "react-router-dom";
import { LineItemBar } from "./line-item-bar";
import { LineItemStepper } from "./line-item-stepper";

const SELECTION_STEP = "line-item";

/** Steps that render the bar themselves, in flow above their options. */
const INLINE_BAR_STEPS = new Set(["resolution", "resolution-options"]);

function usePreviousStep(step: string) {
  const [history, setHistory] = useState<{
    step: string;
    previousStep: string | null;
  }>({ step, previousStep: null });

  if (history.step !== step) {
    setHistory({ step, previousStep: history.step });
    return history.step;
  }
  return history.previousStep;
}

export function LineItemLayout() {
  const { pathname } = useLocation();
  const parts = pathname.split("/");
  const group = parts[parts.length - 3];
  const step = parts[parts.length - 1];
  const outlet = useOutlet();
  const previousStep = usePreviousStep(step);
  const hasFloatingBar = step !== SELECTION_STEP && !INLINE_BAR_STEPS.has(step);
  const hasPreviousBar = previousStep != null && previousStep !== SELECTION_STEP;

  return (
    <div className="w-full flex flex-col flex-1 min-h-0">
      <AnimatePresence mode="wait">
        {group === "line-item" && step !== "confirmation" && (
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1, transition: { type: "spring" } }}
            exit={{ y: -50, opacity: 0 }}
            // Shares its row with the widget chrome (language + close), pinned
            // top-right on the same 32px band. The stepper stretches to whatever
            // width it is given (its dividers are `flex-1`), so the clearance is
            // the padding: pr-32 lands the last step ~35px short of the chrome,
            // and still leaves far more than the three steps need.
            className={cn(
              "absolute top-6 left-8 right-6 z-10 flex min-h-8 items-center bg-background rounded-lg pr-32 @container",
            )}
          >
            <LineItemStepper />
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence mode="wait" propagate>
        <motion.div
          key={pathname}
          className={cn(
            "flex-1 min-h-0 overflow-y-auto overflow-x-hidden isolate",
            hasFloatingBar && "pb-24",
          )}
        >
          {/* Lives with its page so the outgoing bar unmounts in the commit
              that mounts the next one: motion only hands a `layoutId` over to
              a bar that is still there, or just removed, at that moment. */}
          {hasFloatingBar && <LineItemBar slideIn={!hasPreviousBar} />}
          {outlet}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
